/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import * as dateFnsLocales from 'date-fns/locale';
import React, {
  useCallback,
  useMemo,
} from 'react';
import Text from '@components/common/Text';
import fb from '@utils/firebase';
import globals from '@utils/globals';
import i18nFun from '@utils/utilities/i18n';
import useTypedSelector from './useTypedSelector';

const DEBUG = false;

export default function useI18n(localeId=undefined) {
  const i18n = useTypedSelector((state) => state.i18n);

  const currLocaleId = useTypedSelector((state) => localeId || state.userSettings.locale_id || 1);

  const localStaticI18n = useMemo(() => i18nFun(currLocaleId), [currLocaleId]);
  const englishLocalStaticI18n = useMemo(() => currLocaleId === 1 ? {} : i18nFun(1), [currLocaleId]); // no need to recalculate english if that's the current locale

  const remoteDynamicI18n = useMemo(() => {
    const i18nData = i18n?.[currLocaleId]?.i18n || [];
    return i18nData.reduce((acc, i) => {
      if (i.is_function) {
        acc[i.key] = (...args) => {
          let index = 0;
          return i.value.replace(/\{\{\}\}/gi, () => {
            const arg = args[index];
            index++;
            return arg || '';
          });
        };
      } else {
        acc[i.key] = i.value;
      }
      return acc;
    }, {});
  }, [i18n, currLocaleId]);

  const allI18n = useMemo(() => ({
    ...englishLocalStaticI18n, // @NOTE: this is used as a fallback for any missing keys in other locales
    ...localStaticI18n,
    ...remoteDynamicI18n,
  }), [localStaticI18n, remoteDynamicI18n, englishLocalStaticI18n]);

  const i18nCallback: any = useCallback((key, ...params) => {
    let value = allI18n[key];
    if (value === undefined) {
      if (Object.keys(remoteDynamicI18n).length) {
        // don't throw error if this locale i18n translations haven't been fetched yet
        const errorMessage = `\`${key}\` is not a valid key for i18n`;
        fb.recordError(fb.crashlytics, new Error(errorMessage));
      }
      value = '';
    } else if (typeof value === 'function') {
      value = value(...params);
    }

    if (DEBUG && globals.env !== 'prod') {
      return <Text style={{display: 'inline', backgroundColor: '#ff4488'} as any}>{value}</Text>;// @TODO: display okay for native?
    }

    return value;
  }, [allI18n, remoteDynamicI18n]);

  i18nCallback.dateFnsLocale = useTypedSelector((state) => dateFnsLocales[state.locales[currLocaleId]?.datefns_code] || dateFnsLocales.enUS); // @TODO: this is hacky to add a property to a function before returning it

  return i18nCallback;
}
